// Whole-collection client for the promoted `single_matches` + `planned_matches`
// entities (Stage 2). On the http backend these hit the dedicated
// /single-matches and /planned-matches endpoints (real Postgres tables); on
// memory/tests they fall back to the key/value Storage the app used before. The
// app keeps its load-all / save-all model — see docs/architecture/SCHEMA.md.
//
// IMPORTANT: EVERY writer of these lists must go through persistSingleMatches /
// persistPlannedMatches (not Storage directly), or records split between the
// table and the /kv blob.
import { Storage } from '../storage';
import { apiFetch } from './http';
import { WholeCollectionWriteClosed } from './players';
import { descNullsFirst, fetchAllPages } from './pagedList';

const SINGLE_KEY = 'single_matches_v1';
const PLANNED_KEY = 'planned_matches_v1';

export async function fetchSingleMatches(): Promise<any[]> {
  if (Storage.backend === 'http') {
    // IN PAGES (#1202), then the order the single read used: newest played first.
    return fetchAllPages<any>('/single-matches', 'singleMatches', descNullsFirst('playedAt'));
  }
  const r = await Storage.get(SINGLE_KEY);
  return r?.value ? JSON.parse(r.value) : [];
}

// ── SINGLE MATCHES ARE COMMANDS ON THE API BACKEND (#245) ────────────
//
// Registering a result moves the match AND four player ratings, and the browser
// did those as two sequential writes — so a failure in between stored a match
// claiming a rating change that never happened. Both now travel as one intent
// and land in one transaction.
//
// On memory/Supabase the blob path is UNCHANGED: it is what the deployed
// browser runs, and this change is about moving authority on the API path.

/** True when single-match writes must go through the server commands. */
export const singleMatchesAreServerAuthoritative = () => Storage.backend === 'http';

const failed = async (res: Response, fallback: string) => {
  const body = (await res.json().catch(() => null)) as { error?: string; step?: string } | null;
  // The server maps the engine's Norwegian user text to a stable code; the app
  // shows its own message rather than a raw status.
  return new Error(body?.error ? `${fallback} (${body.error})` : `${fallback}: ${res.status}`);
};

/** Register a result. The ENGINE computes the rating change, not the caller. */
export async function createSingleMatchCmd(input: Record<string, any>): Promise<any> {
  const res = await apiFetch('/single-matches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await failed(res, 'Kunne ikke lagre enkeltkamp');
  return res.json();
}

/** Correct a result: the server reverses the old change and applies the new. */
export async function patchSingleMatch(id: string, input: Record<string, any>): Promise<any> {
  const res = await apiFetch(`/single-matches/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await failed(res, 'Kunne ikke oppdatere enkeltkamp');
  return res.json();
}

/** Delete a result and reverse its rating change, atomically. */
export async function deleteSingleMatchCmd(id: string): Promise<void> {
  const res = await apiFetch(`/single-matches/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) throw await failed(res, 'Kunne ikke slette enkeltkamp');
}

export async function persistSingleMatches(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    // The whole-collection PUT is gone (#245): it replaced the table from a
    // list read earlier, so a portal save erased matches the pad had recorded.
    // Throwing here means an unconverted caller fails at the call site rather
    // than at a 405 it did not expect.
    throw new Error('[single-matches] whole-collection write removed (#245) — use the commands');
  }
  await Storage.set(SINGLE_KEY, JSON.stringify(list));
}

export async function fetchPlannedMatches(): Promise<any[]> {
  if (Storage.backend === 'http') {
    // IN PAGES (#1202), then the order the single read used: newest created first.
    return fetchAllPages<any>('/planned-matches', 'plannedMatches', descNullsFirst('createdAt'));
  }
  const r = await Storage.get(PLANNED_KEY);
  return r?.value ? JSON.parse(r.value) : [];
}

// ── NARROW PLANNED-MATCH COMMANDS (#245) ─────────────────────────────
//
// On the API backend each of these sends an INTENT and the server touches the
// rows that intent names. The whole-collection PUT is gone: it upserted from a
// list the client had read earlier, so a pad that deleted a plan on
// finalisation had it put back — making the same enkeltmatch finalisable twice.
//
// On memory/Supabase there is no such API, so they fall back to the
// read-modify-write the app has always done. That path is UNCHANGED on purpose:
// it is what the deployed browser still runs, and this change is about moving
// authority on the API path, not about altering what ships today.

async function mutateLocally(change: (list: any[]) => any[]): Promise<any[]> {
  const r = await Storage.get(PLANNED_KEY);
  const list = r?.value ? (JSON.parse(r.value) as any[]) : [];
  const next = change(list);
  await Storage.set(PLANNED_KEY, JSON.stringify(next));
  return next;
}

/** Create the plan for a booking, replacing any plan that booking already had. */
export async function createPlannedMatch(plan: Record<string, any>): Promise<any> {
  if (Storage.backend === 'http') {
    const res = await apiFetch('/planned-matches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(plan),
    });
    if (!res.ok) throw new Error('Kunne ikke lagre planlagt kamp: ' + res.status);
    return await res.json();
  }
  // The id is minted by the server on the API path; here the caller's stands.
  await mutateLocally((list) => [plan, ...list.filter((p) => p.bookingId !== plan.bookingId)]);
  return plan;
}

/** Change a plan's players, or its live score. Nothing else. */
export async function patchPlannedMatch(
  id: string,
  patch: { team1?: any; team2?: any; liveScore?: any },
): Promise<void> {
  if (Storage.backend === 'http') {
    const res = await apiFetch('/planned-matches/' + encodeURIComponent(id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error('Kunne ikke oppdatere planlagt kamp: ' + res.status);
    return;
  }
  await mutateLocally((list) => list.map((p) => (p.id === id ? { ...p, ...patch } : p)));
}

export async function deletePlannedMatchById(id: string): Promise<void> {
  if (Storage.backend === 'http') {
    const res = await apiFetch('/planned-matches/' + encodeURIComponent(id), { method: 'DELETE' });
    if (!res.ok) throw new Error('Kunne ikke slette planlagt kamp: ' + res.status);
    return;
  }
  await mutateLocally((list) => list.filter((p) => p.id !== id));
}

/** The cascade when bookings are deleted. */
export async function deletePlannedMatchesForBookings(bookingIds: string[]): Promise<void> {
  if (bookingIds.length === 0) return;
  if (Storage.backend === 'http') {
    const q = bookingIds.map((b) => 'bookingId=' + encodeURIComponent(b)).join('&');
    const res = await apiFetch('/planned-matches?' + q, { method: 'DELETE' });
    if (!res.ok) throw new Error('Kunne ikke slette planlagte kamper: ' + res.status);
    return;
  }
  const ids = new Set(bookingIds);
  await mutateLocally((list) => list.filter((p) => !ids.has(p.bookingId)));
}

/**
 * Write the whole planned-match list. CLOSED on the http backend (#194/#245).
 *
 * It used to send the collection to `PUT /planned-matches` — which the server
 * answers `405 whole_collection_write_removed` — and throw a generic `Error`.
 * That is the one shape the other three collection writers were deliberately
 * moved away from: the callers of these functions swallow write errors by
 * design, so a quiet 405 reads exactly like a successful save.
 *
 * It refuses client-side now, with the same error class as players, activities
 * and single matches, so all four behave identically. The blob path is
 * unchanged — it is still how memory/Supabase round-trips the list.
 */
export async function persistPlannedMatches(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    throw new WholeCollectionWriteClosed(
      '[planned-matches] Whole-collection writes are closed (#245). Use a command: ' +
        'createPlannedMatch, patchPlannedMatch, deletePlannedMatchById, ' +
        'deletePlannedMatchesForBookings.',
    );
  }
  await Storage.set(PLANNED_KEY, JSON.stringify(list));
}
